// Matches for a team (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/teams-and-matches.md
// "2. Matches"). The same module as a player's: drop a team on it (or pick one in its search) and it
// lists the team's games. A bar per game, coloured by the result as the League Table's form squares,
// its goals (green for, red against, as a player's Team Goals) and red cards at their minute; the
// score and both sides' xG beside it. Home / Away chips under the chart pick those games out, as a
// player's position chips do. The games and scores are the League Table's own season results
// (`/matches`); the xG, goals and reds are /team-match-stats. Not yet: cup games.

import { Suspense, useMemo, type MouseEvent as ReactMouseEvent } from "react";
import { getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainTeamMatches } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useLeagueTable, useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { LivePartBoundary } from "../live/LiveBoundary.js";
import { LiveError } from "../live/LoadState.js";
import { loadSeasonMatches } from "../live/results.js";
import { loadTeamMatchDetail, momentTooltip, xgLine, type TeamMatchDetail } from "../live/team-matches.js";
import { dotLeftPct, guideLines } from "./matches-view.js";
import { gameDate, gameResult, teamGames, venueCounts, type TeamGame, type TeamRef, type Venue } from "../live/team.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";
import { startMatchCardDragOnMove } from "../canvas/menu-drag.js";
import { matchOfResult, type MatchRef } from "../live/match.js";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Centre the shared tooltip on the hovered element, as a player's Matches rows. */
function tipHandlers(text: string) {
  return {
    onMouseEnter: (e: ReactMouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      showTooltipAt(text, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },
    onMouseLeave: () => hideTooltip(),
  };
}

export function TeamMatches({ mod, team, onChange }: { mod: HTMLElement; team: TeamRef; onChange: () => void }) {
  const league = useLiveLeagueChoices().find((c) => c.id === team.competitionId) ?? null;
  // The league's seasons, newest first (the League Table's own list).
  const seasonCodes = useLeagueTable(team.competitionId)?.seasonCodes ?? [];
  const stored = getModState<string>(mod, "_teamMatchesSeason") ?? team.season;
  const seasonCode = stored && seasonCodes.includes(stored) ? stored : (seasonCodes[0] ?? null);
  const leagueName = league?.label ?? "";
  const items = useMemo<StyledSelectItem[]>(
    () => seasonCodes.map((code) => ({ value: code, label: leagueName ? `${humanizeSeasonCode(code)} · ${leagueName}` : humanizeSeasonCode(code) })),
    [seasonCodes, leagueName],
  );
  const select = (
    <SubControlsSlot mod={mod}>
      {seasonCode !== null && (
        <StyledSelect
          items={items}
          value={seasonCode}
          onChange={(next) => {
            setModState(mod, "_teamMatchesSeason", next);
            onChange();
          }}
          wrapClass="season-select-wrap"
          extraClass="season-select"
        />
      )}
    </SubControlsSlot>
  );
  if (seasonCode === null) {
    return (
      <>
        {select}
        <div className="stats-empty">No seasons for {team.name}.</div>
      </>
    );
  }
  // One season failing to load keeps the season list, so another can be picked (as a player's).
  return (
    <LivePartBoundary
      key={`${team.id}:${team.competitionId}:${seasonCode}`}
      mod={mod}
      fallback={(retry) => (
        <>
          {select}
          <LiveError onRetry={retry} />
        </>
      )}
    >
      {select}
      <TeamMatchesSeason mod={mod} team={team} seasonCode={seasonCode} onChange={onChange} />
    </LivePartBoundary>
  );
}

function TeamMatchesSeason({ mod, team, seasonCode, onChange }: { mod: HTMLElement; team: TeamRef; seasonCode: string; onChange: () => void }) {
  const matches =
    useLiveQuery(`matches:season:${team.competitionId}:${seasonCode}`, () => loadSeasonMatches(liveApi(), team.competitionId, seasonCode)) ?? [];
  // Newest first, as a player's.
  const games = useMemo(() => [...teamGames(matches, team.id, todayIso()).played].reverse(), [matches, team.id]);
  const counts = venueCounts(games);
  // A chip clicked: those games stay bright. Kept on the module (Undo, dashboards); click again to clear.
  const venue = getModState<Venue>(mod, "_teamMatchesVenue") ?? null;
  const pickVenue = (v: Venue | null) => {
    setModState(mod, "_teamMatchesVenue", v === venue ? undefined : (v ?? undefined));
    onChange();
  };
  const season = humanizeSeasonCode(seasonCode);

  if (!games.length) return <div className="stats-empty">No games for {team.name} in {season}.</div>;

  // A game's bar drags out as the match (the Match module).
  const matchOf = (g: TeamGame): MatchRef | null => {
    const m = matches.find((x) => x.matchId === g.matchId);
    return m ? matchOfResult(m, team.competitionId, seasonCode) : null;
  };
  const chart = { mod, team, season, games, counts, venue, pickVenue, matchOf };
  // The games and scores show at once; the xG, goals and reds join them when they've loaded. If
  // they fail, the games stay (the error is logged).
  const plain = <TeamGamesChart {...chart} detail={null} />;
  return (
    <Suspense fallback={plain}>
      <LivePartBoundary mod={mod} fallback={() => plain}>
        <TeamGamesWithDetail {...chart} seasonCode={seasonCode} />
      </LivePartBoundary>
    </Suspense>
  );
}

interface ChartProps {
  mod: HTMLElement;
  team: TeamRef;
  season: string;
  games: TeamGame[];
  counts: ReturnType<typeof venueCounts>;
  venue: Venue | null;
  pickVenue: (v: Venue | null) => void;
  matchOf: (g: TeamGame) => MatchRef | null;
}

function TeamGamesWithDetail(props: ChartProps & { seasonCode: string }) {
  const { team, seasonCode } = props;
  const detail = useLiveQuery(`matches:team:${team.id}:${team.competitionId}:${seasonCode}`, () =>
    loadTeamMatchDetail(liveApi(), team.id, team.competitionId, seasonCode),
  )!;
  return <TeamGamesChart {...props} detail={detail} />;
}

function TeamGamesChart({ mod, team, season, games, counts, venue, pickVenue, matchOf, detail }: ChartProps & { detail: TeamMatchDetail | null }) {
  const isLit = (g: TeamGame) => venue === null || (venue === "home") === g.home;
  // Past 90 when a game's moments run into stoppage time, as a player's axis.
  const lastMinute = detail ? Math.max(0, ...[...detail.moments.values()].flat().map((m) => m.minute ?? 0)) : 0;
  const axisMax = Math.ceil(Math.max(90, lastMinute + 1) / 10) * 10;
  const guides = guideLines(axisMax);
  return (
    <>
      <ModuleExplainer mod={mod} paragraphs={explainTeamMatches({ team: team.name, season, games: games.length, venue })} />
      <div className="matches-chart-wrap">
        <div className="matches-rows">
          {games.map((g) => {
            const result = gameResult(g);
            const stats = detail?.stats.get(g.matchId);
            const moments = detail?.moments.get(g.matchId) ?? [];
            return (
              <div key={g.matchId} className={`matches-row${isLit(g) ? "" : " matches-row-dim"}`}>
                <div className="matches-opponent">
                  <span className="matches-opponent-name">{g.opponent}</span>
                  <span className="matches-opponent-ha">{g.home ? "H" : "A"}</span>
                </div>
                <div
                  className="matches-track"
                  {...tipHandlers(
                    `${gameDate(g)}: ${result} ${g.us}–${g.them} ${g.home ? "vs" : "at"} ${g.opponent}` +
                      (stats ? `\n${xgLine(stats)} · shots ${stats.for.shots} – ${stats.against.shots}` : ""),
                  )}
                >
                  <div
                    className={`matches-bar team-matches-bar form-${result} matches-bar-draggable`}
                    style={{ left: 0, width: "100%" }}
                    onPointerDown={(e) => {
                      const match = matchOf(g);
                      if (!match || window.innerWidth <= 768 || e.button !== 0) return;
                      // As every row drag: no text selection, and the canvas doesn't start a move.
                      e.preventDefault();
                      e.stopPropagation();
                      startMatchCardDragOnMove(e.nativeEvent, match);
                    }}
                  ></div>
                  <div className="matches-half-line" style={{ left: `${guides.halfPct}%` }}></div>
                  {moments
                    .filter((m) => m.minute !== null)
                    .map((m, k) => (
                      <span
                        key={k}
                        className={`matches-dot${m.kind === "red" ? " team-matches-red" : ""}`}
                        style={{
                          left: `${dotLeftPct(m.minute as number, axisMax)}%`,
                          background: m.kind === "red" ? "#ef4444" : m.forTeam ? "#2ecc71" : "#e74c3c",
                        }}
                        {...tipHandlers(momentTooltip(m))}
                      ></span>
                    ))}
                </div>
                <div className="team-matches-score">
                  <b>
                    {g.us}–{g.them}
                  </b>
                  {stats && <span className="team-matches-xg">{xgLine(stats)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="matches-positions">
        <div className="matches-positions-label">
          Home and away{venue !== null ? " · highlighted in the games above" : " · click one to pick those games out"}
        </div>
        <div className="bio-positions">
          <button type="button" className={`bio-position-chip matches-pos-chip${venue === null ? " on" : ""}`} onClick={() => venue !== null && pickVenue(venue)}>
            All <b>{counts.all.label}</b>
          </button>
          {(["home", "away"] as const).map((v) => (
            <button type="button" key={v} className={`bio-position-chip matches-pos-chip${venue === v ? " on" : ""}`} onClick={() => pickVenue(v)}>
              {v === "home" ? "Home" : "Away"} <b>{counts[v].label}</b>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
